import { FormattedMessage, useIntl } from 'react-intl';

import { AddToSlackButton } from '@/components/global/InstallSlack';
import { useChat } from '@/components/providers/ChatProvider';
import { ViteEnv } from '@/env';
import type { AwsCredentialsForList } from '@/lib/queries/awsCredentials';
import type { AwsRole } from '@/lib/queries/awsRole';
import type { GithubConfig } from '@/lib/queries/githubConfig';
import type { S3Bucket } from '@/lib/queries/terraformBackend';

import { Example } from './Example';
import s3Logo from './logos/aws-s3.svg?url';
import awsLogo from './logos/aws.svg?url';
import githubLogo from './logos/github.svg?url';
import kubernetesLogo from './logos/kubernetes.svg?url';

interface Props {
  awsUsers: AwsCredentialsForList[] | undefined;
  awsRoles: AwsRole[] | undefined;
  githubInstall: GithubConfig[] | undefined;
  buckets: S3Bucket[] | undefined;
}

export const TryIt = ({
  awsUsers,
  awsRoles,
  buckets,
  githubInstall,
}: Props): JSX.Element => {
  const { setPrompt } = useChat();
  const { formatMessage } = useIntl();

  const githubOk = (githubInstall ?? []).length > 0;
  const awsOk =
    (awsRoles ?? []).some(({ status }) => status === 'active') ||
    (awsUsers ?? []).some(({ status }) => status === 'active');
  const bucketsOk = (buckets ?? []).some(
    b => b.syncStatus === 'synced' && b.accessStatus === 'active',
  );
  const kubeOk = false;

  return (
    <>
      <div className="flex flex-row justify-between items-baseline">
        <h2 className="text-2xl mt-8">
          <FormattedMessage id="integrations.tryIt.title" />
        </h2>
        <AddToSlackButton variant="main" />
      </div>
      <div className="flex flex-col gap-2 mt-4">
        <Example
          title="integrations.tryIt.examples.hardcodedValues"
          requirements={[
            {
              id: 'github',
              logoUrl: githubLogo,
              ok: githubOk,
              onClick: () => {
                window.location.href = `https://github.com/login/oauth/authorize?client_id=${ViteEnv.VITE_GITHUB_CLIENT_ID}&scope=read:user,user:email`;
              },
              available: true,
            },
          ]}
          onClick={() => {
            setPrompt(
              formatMessage({
                id: 'integrations.tryIt.examples.hardcodedValues',
              }),
            );
          }}
        />
        <Example
          title="integrations.tryIt.examples.vpcIdList"
          requirements={[
            {
              id: 'aws',
              logoUrl: awsLogo,
              ok: awsOk,
              available: true,
              to: '/integrations/aws',
            },
          ]}
          onClick={() => {
            setPrompt(
              formatMessage({
                id: 'integrations.tryIt.examples.vpcIdList',
              }),
            );
          }}
        />
        <Example
          title="integrations.tryIt.examples.administratorAccessWithCode"
          requirements={[
            {
              id: 'github',
              logoUrl: githubLogo,
              ok: githubOk,
              onClick: () => {
                window.location.href = `https://github.com/login/oauth/authorize?client_id=${ViteEnv.VITE_GITHUB_CLIENT_ID}&scope=read:user,user:email`;
              },
              available: true,
            },
            {
              id: 'aws',
              logoUrl: awsLogo,
              ok: awsOk,
              to: '/integrations/aws',
              available: true,
            },
            {
              id: 'buckets',
              logoUrl: s3Logo,
              ok: bucketsOk,
              available: true,
              to: '/integrations/terraform-backend/buckets',
            },
          ]}
          onClick={() => {
            setPrompt(
              formatMessage({
                id: 'integrations.tryIt.examples.administratorAccessWithCode',
              }),
            );
          }}
        />
        <Example
          title="integrations.tryIt.examples.podsStuckOnPending"
          requirements={[
            {
              id: 'kube',
              logoUrl: kubernetesLogo,
              ok: kubeOk,
              available: false,
            },
          ]}
        />
      </div>
    </>
  );
};
